import { Button } from "@/components/shared/Button/Button";
import { EmptyCupons } from "./EmptyCupons";
import CupomCard from "./CupomCard";
import {
  useCouponCheckouts,
  useCouponOffers,
  useFetchCoupons,
} from "./hooks/useFetchCupons";
import { CouponForm } from "./CouponForm";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import useModalStore from "@/store/useModalStore";
import { useMemo, useState } from "react";
import { ProductDetailsResponse, ProductRole } from '@/@types/Products';
import { Coupon } from '@/@types/Coupons';
import { PaginationRange } from '@/components/shared/PaginationRange/PaginationRange';
import CustomSheet from '@/components/shared/Sheet/Sheet';
import { useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CouponsService } from '@/services/coupons.service';
import { buildCouponLinks, isCouponLinkable, toCouponOffer } from './couponLink';
import {
  couponCurrencyFromApi,
  couponScopeCurrencies,
  normalizeCouponCurrency,
} from './couponScope';

export function CuponsPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const { coupons, totalPages, hasPagination, isLoading } =
    useFetchCoupons(currentPage);
  const { modals, openModal, closeModal } = useModalStore();
  const { t } = useTranslation('common');
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  /** Remonta o form a cada abertura: `reset()` não limpa os inputs de valor. */
  const [createFormKey, setCreateFormKey] = useState(0);

  const { product } = useOutletContext<{ product: ProductDetailsResponse }>();

  const offers = useCouponOffers();
  const checkouts = useCouponCheckouts();
  const couponOffers = useMemo(() => offers.map(toCouponOffer), [offers]);

  /** Só com ofertas em BRL e USD um cupom de valor fixo precisa de conversão. */
  const isMultiCurrency = couponScopeCurrencies([], couponOffers).length > 1;

  // Mesma chave do formulário do cupom: reaproveita o cache dele.
  const { data: usdToBrl } = useQuery({
    queryKey: ['currency-rate', 'USD', 'BRL'],
    queryFn: async () =>
      await CouponsService.getCurrencyRate({ from: 'USD', to: 'BRL' }),
    enabled: isMultiCurrency,
    staleTime: 5 * 60 * 1000,
  });

  /** Cupom excluído é soft delete: a API ainda o devolve, mas ele não aparece. */
  const visibleCoupons = coupons.filter((coupon) => coupon.status !== 'deleted');

  const resolveCouponLinks = (coupon: Coupon) =>
    buildCouponLinks(coupon, checkouts, couponOffers, usdToBrl);

  /** Cupom antigo não tem `currency`: cai na moeda da oferta a que está preso. */
  const resolveCurrency = (coupon: Coupon): string => {
    const fromApi = couponCurrencyFromApi(coupon.currency);

    if (fromApi) {
      return fromApi;
    }

    const offer = offers.find((offer) =>
      (coupon.offerIds ?? []).some(
        (offerId) => String(offer.id) === String(offerId),
      ),
    );

    return normalizeCouponCurrency(offer?.currency ?? offers[0]?.currency);
  };

  const handleCreateClick = () => {
    setCreateFormKey((key) => key + 1);
    openModal('createCoupon');
  };

  const handleEditClick = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    openModal('editCoupon');
  };

  return (
    <>
      <CustomSheet
        open={modals['createCoupon']}
        onClose={() => closeModal('createCoupon')}
      >
        <CouponForm key={createFormKey} />
      </CustomSheet>

      {selectedCoupon && (
        <CustomSheet
          open={modals['editCoupon']}
          onClose={() => closeModal('editCoupon')}
        >
          <CouponForm key={selectedCoupon.id} coupon={selectedCoupon} />
        </CustomSheet>
      )}

      <section className='mt-8 mx-auto'>
        <div className='flex md:items-center mb-8 flex-col md:flex-row w-full p-4 justify-between max-md:gap-3'>
          <p className='text-base font-semibold'>{t('couponsPage.title')}</p>

          {product?.relationship?.role === ProductRole?.PRODUCER && (
            <Button
              className='w-full md:max-w-[133px]'
              onClick={handleCreateClick}
              variant='primary'
              data-cy='btn-open-create-coupon'
            >
              <Plus />
              {t('couponsPage.createButton')}
            </Button>
          )}
        </div>
      </section>

      <div>
        {visibleCoupons.length === 0 && !isLoading ? (
          <EmptyCupons />
        ) : (
          <>
            <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6'>
              {visibleCoupons.map((coupon) => (
                <CupomCard
                  key={coupon.id}
                  coupon={coupon}
                  currency={resolveCurrency(coupon)}
                  couponLinks={resolveCouponLinks(coupon)}
                  linkable={isCouponLinkable(coupon)}
                  onEdit={() => handleEditClick(coupon)}
                  ctaDisabled={
                    product?.relationship?.role !== ProductRole.PRODUCER
                  }
                />
              ))}
            </div>

            {hasPagination && (
              <PaginationRange
                className='mt-8'
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
